import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import type { DocsIdTypeData } from '@/shared/constant/DocsIdTypeData';
import { ErrorCode } from '@/shared/constant/error/ErrorCode';
import type { ErrorCodeMessageType } from '@/shared/constant/error/ErrorCodeMessageType';
import { OnlineType } from '@/shared/constant/OnlineType';
import { UtmCampaign } from '@/shared/constant/utm';
import { useLocation } from '@remix-run/react';
import { Button } from 'aui';
import { FoxIconRefreshCw01 } from 'aui-biz-icons';
import React from 'react';
import { FooterSlogan } from '../footer-slogan/FooterSlogan';
import { CustomDomainError } from './components/custom-domain-error/CustomDomainError';
import { DomainCanNotAccessError } from './components/domain-can-not-access-error/DomainCanNotAccessError';
import { ExpiredError } from './components/expired-error/ExpiredError';
import { InvalidPathError } from './components/invalid-path-error/InvalidPathError';
import { NeedPreviewError } from './components/need-preview-error/NeedPreviewError';
import { NetworkError } from './components/network-error/NetworkError';
import { NotExistedError } from './components/not-existed-error/NotExistedError';
import { UnauthorizedError } from './components/unauthorized-error/UnauthorizedError';
import { UnknownError } from './components/unknown-error/UnknownError';
import { UnpublishError } from './components/unpublish-error/UnpublishError';

export type ErrorContentProps = {
  errorCodeMessage: ErrorCodeMessageType;
  isShowReloadButton?: boolean;
  docsIdTypeData: DocsIdTypeData | undefined;
};

export function ErrorContent(props: ErrorContentProps) {
  const { errorCodeMessage, isShowReloadButton, docsIdTypeData } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const appHomepage = APP_HOMEPAGE;
  const appName = APP_NAME;

  const pathname = useLocation().pathname;
  const isPreview = React.useMemo(() => {
    return pathname.endsWith('/preview');
  }, [pathname]);

  const utmCampaign = React.useMemo(() => {
    if (errorCodeMessage.errorCode === ErrorCode.Unauthorized) {
      return UtmCampaign.DocsLoginPageFooter;
    }
    return UtmCampaign.ErrorPage;
  }, [errorCodeMessage.errorCode]);

  return (
    <div className="flex h-full w-full flex-col overflow-auto">
      <div className="flex flex-1 flex-col items-center justify-center">
        {errorCodeMessage.errorCode === ErrorCode.CustomDomainError && (
          <CustomDomainError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.Unauthorized && (
          <UnauthorizedError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.Unpublish && (
          <UnpublishError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.NotExisted &&
          (isPreview ? (
            <NeedPreviewError value={errorCodeMessage} />
          ) : (
            <NotExistedError value={errorCodeMessage} />
          ))}

        {errorCodeMessage.errorCode === ErrorCode.Unknown && (
          <UnknownError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.NetworkError && (
          <NetworkError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.InvalidPath && (
          <InvalidPathError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.Expired && (
          <ExpiredError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.DomainCanNotAccess && (
          <DomainCanNotAccessError value={errorCodeMessage} />
        )}

        {isShowReloadButton && (
          <Button
            type="primary"
            theme="primary"
            className="mt-8"
            onClick={() => {
              window.location.reload();
            }}
            icon={<FoxIconRefreshCw01 />}
          >
            {clientI18n('onlineShare.resource.notExisted.reload')}
          </Button>
        )}
      </div>

      {!errorCodeMessage?.hideBuiltWith && (
        <div className="flex flex-col items-center justify-center p-8 text-base font-base text-secondary">
          <FooterSlogan
            homeIndexUrl={appHomepage}
            title={appName}
            utmCampaign={utmCampaign}
            onlineType={docsIdTypeData?.onlineType ?? OnlineType.DocsSite}
          />
        </div>
      )}
    </div>
  );
}
